import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, X, Loader2 } from "lucide-react";

// Selection state for a list of ids (typically the currently-visible/filtered ids).
export function useBulkSelect() {
  const [selected, setSelected] = useState(() => new Set());
  const toggle = useCallback((id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  }), []);
  const clear = useCallback(() => setSelected(new Set()), []);
  const toggleAll = useCallback((ids) => setSelected((prev) => {
    const allNow = ids.length > 0 && ids.every((id) => prev.has(id));
    return allNow ? new Set() : new Set(ids);
  }), []);
  const isSelected = useCallback((id) => selected.has(id), [selected]);
  return { selected, setSelected, toggle, clear, toggleAll, isSelected };
}

// Header "select all" checkbox for a table.
export function SelectAllCheckbox({ ids, selected, onToggleAll }) {
  const all = ids.length > 0 && ids.every((id) => selected.has(id));
  return (
    <Checkbox
      checked={all}
      onCheckedChange={() => onToggleAll(ids)}
      aria-label="Select all"
      data-testid="bulk-select-all"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// Floating toolbar shown when at least one row is selected.
export function BulkBar({ count, onClear, onDelete, canDelete, deleting, children, deleteLabel = "Delete" }) {
  if (!count) return null;
  return createPortal(
    <div
      className="fixed bottom-4 left-3 right-3 z-50 flex flex-col gap-2.5 rounded-2xl border border-border bg-white/95 px-4 py-3 shadow-xl backdrop-blur animate-fade-up sm:bottom-6 sm:left-1/2 sm:right-auto sm:max-w-max sm:-translate-x-1/2 sm:flex-row sm:items-center sm:gap-3 sm:rounded-full sm:py-2.5"
      data-testid="bulk-action-bar"
    >
      <div className="flex w-full items-center justify-between sm:w-auto">
        <span className="text-sm font-semibold text-slate-900" data-testid="bulk-selected-count">
          {count} Selected
        </span>
        <button onClick={onClear} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 sm:hidden" title="Clear selection" data-testid="bulk-clear-button-mobile">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="hidden h-5 w-px bg-border sm:block" />
      <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:items-center sm:gap-2">
        {children}
        {canDelete && (
          <Button size="sm" variant="destructive" onClick={onDelete} disabled={deleting} data-testid="bulk-delete-button" className="w-full sm:w-auto">
            {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
            {deleteLabel}
          </Button>
        )}
      </div>
      <button
        onClick={onClear}
        className="ml-1 hidden rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 sm:block"
        title="Clear selection"
        data-testid="bulk-clear-button"
      >
        <X className="h-4 w-4" />
      </button>
    </div>,
    document.body
  );
}
